import React, { useEffect, useState } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Button,
} from '@mui/material'
import {
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon,
} from '@mui/icons-material'
import { WhatsAppStatusCard } from '../whatsapp/WhatsAppStatusCard'
import { WhatsAppCredentialsDialog } from '../whatsapp/WhatsAppCredentialsDialog'
import { useAuth } from '../../contexts/AuthContext'
import { smbsApi } from '../../services/api'
import { useNotification } from '../../contexts/NotificationContext'

interface Channel {
  id: string
  name: string
  type: 'whatsapp' | 'instagram'
  isConnected: boolean
}

const ChannelStatus: React.FC = () => {
  const { user } = useAuth()
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false)
  const [activeSmbId, setActiveSmbId] = useState<string | null>(null)
  const [loadingSmb, setLoadingSmb] = useState(false)
  const { showNotification } = useNotification()

  const handleConfigureCredentials = () => {
    if (!activeSmbId) {
      showNotification('Select a business to configure WhatsApp credentials.', 'warning')
      return
    }
    setCredentialsDialogOpen(true)
  }

  useEffect(() => {
    if (!user) {
      setActiveSmbId(null)
      return
    }

    if (user.role === 'smb') {
      setActiveSmbId(user.id)
      return
    }

    const loadAgencySmb = async () => {
      setLoadingSmb(true)
      try {
        const response = await smbsApi.getSMBs({ per_page: 1, page: 1, agency_id: user.id })
        const list = response?.smbs || response?.items || []
        if (list.length > 0 && list[0].id) {
          setActiveSmbId(list[0].id)
        } else {
          setActiveSmbId(null)
        }
      } catch (error) {
        console.error('Failed to load default SMB for channel status', error)
        setActiveSmbId(null)
      } finally {
        setLoadingSmb(false)
      }
    }

    loadAgencySmb()
  }, [user])
  
  // Mock data for Instagram (WhatsApp will use real component)
  const instagramChannel: Channel = {
    id: 'instagram_001',
    name: 'Instagram Business',
    type: 'instagram',
    isConnected: true,
  }

  const getChannelIcon = (type: string) => {
    switch (type) {
      case 'whatsapp':
        return <WhatsAppIcon sx={{ color: '#25D366' }} />
      case 'instagram':
        return <InstagramIcon sx={{ color: '#E4405F' }} />
      default:
        return null
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* WhatsApp Integration - Using real component */}
      {loadingSmb ? (
        <Card>
          <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </CardContent>
        </Card>
      ) : activeSmbId ? (
        <WhatsAppStatusCard
          smbId={activeSmbId}
          onConfigureCredentials={handleConfigureCredentials}
        />
      ) : (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="h6">WhatsApp Integration</Typography>
              <Typography variant="body2" color="text.secondary">
                Select a business to view WhatsApp status and manage credentials.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<WhatsAppIcon />}
                onClick={() => showNotification('Open a business profile to manage WhatsApp credentials.', 'info')}
                sx={{ alignSelf: 'flex-start' }}
              >
                Manage Businesses
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      
      {/* Instagram Integration - Mock for now */}
      <Card>
        <CardContent>
          <Typography variant="h6" component="h3" fontWeight="bold" sx={{ mb: 2 }}>
            Instagram Status
          </Typography>
          
          <List disablePadding>
            <ListItem disablePadding sx={{ py: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                {getChannelIcon(instagramChannel.type)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight="medium">
                    {instagramChannel.name}
                  </Typography>
                }
              />
              <Chip
                label={instagramChannel.isConnected ? 'Connected' : 'Disconnected'}
                color={instagramChannel.isConnected ? 'success' : 'error'}
                variant="outlined"
                size="small"
              />
            </ListItem>
          </List>

          {/* Connection Quality Indicator */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  bgcolor: 'success.main',
                  borderRadius: '50%',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                }}
              />
              <Typography variant="body2" color="success.dark" fontWeight="medium">
                Instagram operational
              </Typography>
            </Box>
            <Typography variant="caption" color="success.dark" sx={{ mt: 0.5, display: 'block' }}>
              Last sync: 2 minutes ago
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* WhatsApp Credentials Dialog */}
      {activeSmbId && (
        <WhatsAppCredentialsDialog
          open={credentialsDialogOpen}
          onOpenChange={setCredentialsDialogOpen}
          smbId={activeSmbId}
          onSuccess={() => {
            setCredentialsDialogOpen(false)
            window.dispatchEvent(new CustomEvent('whatsapp-credentials-updated', { detail: { smbId: activeSmbId } }))
          }}
        />
      )}
    </Box>
  )
}

export default ChannelStatus
